import React, { useEffect, useImperativeHandle, useState } from 'react'
import { Header, Table, Pagination, Button, Placeholder, Modal, Input } from 'react-frontier2'
import { ErrorHeader } from '../../components'
import { addCommas, bindClick, fromUnixLocal } from '@arema/components/Util'
import { PdvCorte } from '../../AdminClasses'
import { Money } from '@arema/components'
import API from '../../API'
import CorteDetailModal from "./CorteDetailModal";
import ConciliaOrdersModal from "./ConciliaOrdersModal";
import { useTranslation } from 'react-i18next'

interface PDVCorteListProps {
	pdv_id: number,
}

enum MODALS {
	CORTE_DETAIL = 100,
	CONCILIA_ORDERS = 101,
	SEARCH_CORTE = 102,
}

export interface CorteListElement{
	reload: ()=>void,
}

var PdvCorteList =  React.forwardRef((props: PDVCorteListProps, ref: React.Ref<CorteListElement>)=>{
	var { t } = useTranslation();
	var [loadError, setLoadError] = useState<string>(null);
	var [cortes, setCortes] = useState<PdvCorte[]>(null);
	var [page, setPage] = useState<number>(0)
	var [selectedCorte, setSelectedCorte] = useState<PdvCorte>(null)
	var [modal, setModal] = useState<MODALS>(null)
	var [searching, setSearching] = useState<boolean>(null);
	var [search, setSearch] = useState<string>('');
	var [searchResults, setSearchResults] = useState<{
		query: string,
		data: PdvCorte[],
	}>(null);
	const PAGE_SIZE = 15;

	useEffect(() => {
		if(props.pdv_id && !isNaN(props.pdv_id)) {
			loadData(page)
		}
	}, [props.pdv_id])

	useImperativeHandle(ref, ()=>({
		reload: ()=>{
			setCortes(null);
			setPage(0);
			loadData(0);
			setSearchResults(null);
			setSearch('');
		}
	}), [props.pdv_id]);



	var loadData = (page?: number) => {
		setCortes(null);
		API.getPdvConciliaCortes(props.pdv_id, null, true, page * PAGE_SIZE, PAGE_SIZE).then(res => {
			if (res.error) return setLoadError(res.message);
			setCortes(res.data);
		}).catch(err => {
			setLoadError('Error inesperado cargando los cortes (LCL-1)');
		})
	}

	var searchCorte = (query: string)=>{
		setSearching(true);
		setModal(null);
		API.getPdvConciliaCortes(props.pdv_id, query, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setSearchResults({
				query,
				data: res.data
			});
		}).catch(err => {
			setLoadError('Error inesperado cargando los cortes (LCL-1)');
		}).finally(()=>{
			setSearching(false);
		})
	}

	var openCorteDetailModal = (corte: PdvCorte) => {
		return () => {
			setSelectedCorte(corte);
			setModal(MODALS.CORTE_DETAIL);
		}
	}

	var openConciliaOrdersModal = (corte: PdvCorte) => {
		return () => {
			setSelectedCorte(corte);
			setModal(MODALS.CONCILIA_ORDERS);
		}
	}

	if (loadError) {
		return <ErrorHeader error={loadError} />
	}

	var onPageChange = (page: number) => {
		setPage(page);
		loadData(page)
	}

	var onConciliaCorte = (corte_id: number, amount: number)=>{
		var c = [...cortes];
		var cx = c.findIndex(a=>a.corte_id===corte_id);
		if(cx!=-1){
			c[cx].concilia_cash = amount;
			c[cx].concilia_percent_cash = c[cx].concilia_cash/c[cx].total_cash;
			setCortes(c);
		}
	}

	var CorteRow = (props: { corte: PdvCorte })=>{
		if(!props.corte) return null;
		var c = props.corte;
		return <Table.Row key={`CRTL-${c.corte_id}`}>
			<Table.Cell collapsing>{c.corte_id}</Table.Cell>
			<Table.Cell>{fromUnixLocal(c.date_start).toFormat('dd/MM/yy HH:mm')}</Table.Cell>
			<Table.Cell collapsing>{addCommas(c.order_count, false)}</Table.Cell>
			<Table.Cell collapsing><Money value={c.total_cash} /></Table.Cell>
			<Table.Cell collapsing><Money value={c.deposit} /></Table.Cell>
			<Table.Cell collapsing><Money value={c.total_cash-c.deposit} /></Table.Cell>
			<Table.Cell collapsing centered className={c.deposit-c.concilia_cash>0 ? 'yellow' : (c.concilia_percent_cash>=1 ? 'green' : null)}>{Math.floor(c.concilia_percent_cash*100)}%</Table.Cell>
			<Table.Cell collapsing>
				<Button icon iconName="ellipsis-h" size="tiny" menu={[
					{ text: 'Ver detalles', onClick: openCorteDetailModal(c) },
					{ text: 'Conciliar', onClick: openConciliaOrdersModal(c) }
				]} />
			</Table.Cell>
		</Table.Row>
	}

	var hasNext = cortes?.length >= PAGE_SIZE;
	return (
		<div style={{ maxWidth: '100%' }} >
			<Table style={{ maxWidth: '100%', minWidth: 800, width: 800, margin: 'auto' }}>
				<Table.Head
					title='Cortes'
					titleRight={(
						<Button text={t('common.search')} iconName={'search'} size={'tiny'} color={'black'} onClick={bindClick(setModal, MODALS.SEARCH_CORTE)} />
					)}
				>
					{searchResults && (
						<Table.Row>
							<Table.Cell header colSpan={100}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 15, fontSize: 14 }}>
									<div>Resultados de "{searchResults.query}"</div>
									<Button text='Vaciar' onClick={bindClick(setSearchResults, null)} size={'tiny'} iconName={'times'} />
								</div>
							</Table.Cell>
						</Table.Row>
					)}
					<Table.Row headerCell data={['ID', 'Fecha', 'Ventas', 'Efectivo', 'Depositado', 'Restante', 'Concilia', '']}></Table.Row>
				</Table.Head>
				<Table.Body>
					{(cortes && !searching) ? (
						searchResults ? (
							searchResults.data.length===0 ? (
								<Table.Cell empty row colSpan={100} value={`No se encontraron cortes con "${searchResults.query}"`} />
							) : searchResults.data.map(c=>(
								<CorteRow corte={c} key={`CRTWSCH-${c.corte_id}`} />
							))
						) : (
							cortes.length===0 ? (
								<Table.Cell empty row colSpan={100} value='No hay historial de cortes en este PDV' />
							) : (
								cortes.map(c=>(
									<CorteRow corte={c} key={`CRTW-${c.corte_id}`} />
								))
							)
						)
					) : new Array(5).fill('A').map((a, i) => (
						<Table.Row key={`crt-${i}`} collapsingIndexes={[0, 1, 2, 4, 5]} data={[
							null,
							<Placeholder width={100} />,
							<Placeholder width={20} />,
							<Placeholder width={20} />,
							<Placeholder width={20} />,
							<Placeholder width={20} />,
							<Placeholder width={20} />,
							null,
						]} />
					))}
				</Table.Body>
				{!!cortes && (cortes.length>=PAGE_SIZE || page>=1) && (
					<Table.Footer actions={(
						<Pagination inline page={page} onPageChange={onPageChange} hasNext={hasNext} />
					)} />
				)}
			</Table>

			<Modal open={modal===MODALS.SEARCH_CORTE} onClose={bindClick(setModal)} size={'mini'}>
				<Modal.Header text={t('common.search')} />
				<Modal.Content>
					<Input label='Corte' comment='Buscar por ID del corte' value={search} onChange={setSearch} onSubmit={()=>searchCorte(search)} />
				</Modal.Content>
				<Modal.Actions>
					<Button text={t('common.close')} basic onClick={bindClick(setModal)} />
					<Button text={t('common.search')} color={'black'} onClick={()=>searchCorte(search)} />
				</Modal.Actions>
			</Modal>

			{selectedCorte && (
				<CorteDetailModal
					corte_id={selectedCorte.corte_id}
					open={MODALS.CORTE_DETAIL === modal}
					onClose={bindClick(setModal)}
				/>)
			}

			{selectedCorte && (
				<ConciliaOrdersModal
					corte_id={selectedCorte.corte_id}
					open={MODALS.CONCILIA_ORDERS === modal}
					onClose={bindClick(setModal)}
					onConcilia={onConciliaCorte}
				/>)
			}
		</div>
	)
});

export default PdvCorteList;